import { useCallback, useEffect, useMemo, useState } from 'react';

import { subscribeToAccessToken } from './accessTokenStore';
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext';
import {
  login,
  logout,
  readSessionHint,
  refreshSessionOutcome,
  sessionIdentity,
  subscribeToSession,
  type AuthenticatedUser,
  type RefreshOutcome,
} from './session';

/** How often an offline start asks the server again, besides the browser's `online` event. */
const OFFLINE_RETRY_MS = 30_000;

/**
 * Resolves and holds the current session.
 *
 * On mount it tries to resume a session from the refresh cookie. That is what makes a page reload
 * keep the user signed in even though the access token lives only in memory: the cookie is the
 * durable half of the session, and the token is re-minted from it. The resume goes through the
 * shared single-flight refresh, so StrictMode's second effect run joins the first exchange instead
 * of presenting the same cookie twice — which the server would rightly read as a stolen credential
 * and answer by revoking the session.
 *
 * It also subscribes to the session identity rather than to the raw token, so it learns not only
 * that a session ended but that it became somebody else's — another tab signing in as a different
 * user resolves here instead of leaving this tab showing the previous one.
 *
 * @param children the application tree
 * @returns the provider element
 */
export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('resolving');
  const [user, setUser] = useState<AuthenticatedUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    void refreshSessionOutcome().then((outcome) => {
      if (cancelled) {
        return;
      }
      // Only the still-unresolved case may be settled by the resume. A sign-in can complete while
      // this is in flight, and the resume then returns null because its generation was superseded
      // — applying that would mark a freshly authenticated user anonymous and throw away the
      // session they just created.
      setStatus((current) => {
        if (current !== 'resolving') {
          return current;
        }
        if (outcome.kind === 'resumed') {
          setUser(outcome.user);
          return 'authenticated';
        }
        // No signal is not a sign-out: the technician who reloads in a basement gets field mode and
        // their queued work, not a sign-in form that cannot be submitted without the network.
        const remembered = outcome.kind === 'unreachable' ? readSessionHint() : null;
        setUser(remembered);
        return remembered === null ? 'anonymous' : 'offline';
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (status !== 'offline') {
      return;
    }
    let stopped = false;
    const settle = (outcome: RefreshOutcome) => {
      if (stopped) {
        return;
      }
      if (outcome.kind === 'resumed') {
        setUser(outcome.user);
        setStatus('authenticated');
      } else if (outcome.kind === 'ended') {
        setUser(null);
        setStatus('anonymous');
      }
    };
    const retry = () => {
      void refreshSessionOutcome().then(settle);
    };
    window.addEventListener('online', retry);
    const timer = window.setInterval(retry, OFFLINE_RETRY_MS);
    return () => {
      stopped = true;
      window.removeEventListener('online', retry);
      window.clearInterval(timer);
    };
  }, [status]);

  // Two different losses, both of which must reach the interface.
  //
  // The session subscription reports a change of principal: a refresh that resumed somebody else,
  // or a sign-out. The token subscription reports the transport giving up on the session from deep
  // inside a request, far from any component — the token is cleared there whether or not anything
  // knows who the session belonged to, and an interface still showing a signed-in user at that
  // point is showing a user who has no access.
  useEffect(
    () =>
      subscribeToSession((next) => {
        setUser(next);
        setStatus(next === null ? 'anonymous' : 'authenticated');
      }),
    [],
  );

  useEffect(
    () =>
      subscribeToAccessToken((token) => {
        if (token === null) {
          setUser(null);
          setStatus((current) => (current === 'resolving' ? current : 'anonymous'));
        }
      }),
    [],
  );

  const signIn = useCallback(
    async (email: string, password: string, workspace?: string): Promise<void> => {
      const signedIn = await login(email, password, workspace);
      setUser(signedIn);
      setStatus('authenticated');
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await logout();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const can = useCallback(
    (permission: string): boolean => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut, can, identity: sessionIdentity(user) }),
    [status, user, signIn, signOut, can],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
